import { Helmet } from 'react-helmet-async'
import { useAuth } from '@/contexts/auth-context'
import { useAuthActions } from '@convex-dev/auth/react'
import { useNavigate } from 'react-router-dom'
import { isElectron } from '@/lib/electron'
import { cn } from '@/lib/utils'
import {
  GraduationCap,
  Scale,
  HeartPulse,
  HardHat,
  Building2,
  Megaphone,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react'

import { Navbar } from '@/components/homepage/sections/Navbar'
import { SectionHeader } from '@/components/homepage/ui/SectionHeader'
import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { CTA } from '@/components/homepage/sections/CTA'
import { CornerPlus } from '@/components/ui/corner-plus'
import { LazyDither } from '@/components/homepage/sections/LazyDither'
import { OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

// ─── Data ─────────────────────────────────────────────────────────────────

interface UseCase {
  title: string
  description: string
}

interface Industry {
  icon: LucideIcon
  title: string
  tagline: string
  description: string
  useCases: UseCase[]
  accent: boolean
}

const industries: Industry[] = [
  {
    icon: GraduationCap,
    title: 'Education',
    tagline: 'Transform how students learn',
    description:
      'Turn lectures, training materials, and campus life into engaging AI-generated video content.',
    accent: false,
    useCases: [
      {
        title: 'Explainer videos for complex topics',
        description:
          'Turn dense course material into clear, engaging visual explanations that students can watch at their own pace.',
      },
      {
        title: 'Virtual campus tours',
        description:
          'Create immersive campus walkthrough videos for prospective students without expensive production crews.',
      },
      {
        title: 'Professional development & training',
        description:
          'Produce consistent, high-quality training content for faculty and staff across departments and campuses.',
      },
    ],
  },
  {
    icon: Scale,
    title: 'Legal',
    tagline: 'Communicate with clarity',
    description:
      'Make complex legal concepts accessible through professional AI-generated video.',
    accent: true,
    useCases: [
      {
        title: 'Case visualization & reconstruction',
        description:
          'Build compelling visual narratives for trial presentations that help juries understand complex sequences of events.',
      },
      {
        title: 'Client education content',
        description:
          'Explain legal processes, rights, and procedures through professional video that builds client confidence.',
      },
      {
        title: 'Firm marketing & thought leadership',
        description:
          'Position your practice with polished video content showcasing expertise across practice areas.',
      },
    ],
  },
  {
    icon: HeartPulse,
    title: 'Healthcare',
    tagline: 'Improve patient outcomes',
    description:
      'Deliver critical information through clear, professional video content at scale.',
    accent: true,
    useCases: [
      {
        title: 'Patient education & discharge instructions',
        description:
          'Deliver critical care instructions through video that patients can review at home, improving compliance and outcomes.',
      },
      {
        title: 'Medical training simulations',
        description:
          'Create realistic training scenarios for medical professionals without the cost of live simulations.',
      },
      {
        title: 'Public health campaigns',
        description:
          'Produce multilingual health awareness content quickly to respond to emerging public health needs.',
      },
    ],
  },
  {
    icon: HardHat,
    title: 'Construction',
    tagline: 'Visualize before you build',
    description:
      'Communicate project vision, safety protocols, and progress with professional video.',
    accent: false,
    useCases: [
      {
        title: 'Project visualization & pre-sales',
        description:
          'Show clients what their finished project will look like before breaking ground, winning more bids.',
      },
      {
        title: 'Safety training & compliance',
        description:
          'Create consistent safety training videos for every jobsite, ensuring all workers receive the same critical information.',
      },
      {
        title: 'Progress reports & stakeholder updates',
        description:
          'Keep investors and clients informed with professional video updates that showcase milestones and timelines.',
      },
    ],
  },
  {
    icon: Building2,
    title: 'Real Estate',
    tagline: 'Sell properties faster',
    description:
      'Generate virtual tours, listing videos, and neighborhood content that converts.',
    accent: false,
    useCases: [
      {
        title: 'Virtual property tours',
        description:
          'Generate stunning walkthrough videos for listings that attract more qualified buyers and reduce time on market.',
      },
      {
        title: 'Neighborhood & lifestyle content',
        description:
          'Showcase the community, amenities, and lifestyle around your listings to help buyers envision their new life.',
      },
      {
        title: 'Pre-construction & renovation previews',
        description:
          'Visualize renovations and new developments before they exist, helping buyers commit with confidence.',
      },
    ],
  },
  {
    icon: Megaphone,
    title: 'Marketing & Advertising',
    tagline: 'Launch campaigns faster',
    description:
      'Produce ad creatives, product demos, and brand stories with AI — at the speed of your ideas.',
    accent: true,
    useCases: [
      {
        title: 'Social media ad creatives',
        description:
          'Produce dozens of ad variations in minutes instead of weeks, testing more concepts with less budget.',
      },
      {
        title: 'Product launch videos',
        description:
          'Create polished launch videos and product demos without coordinating agencies, studios, and post-production.',
      },
      {
        title: 'Brand storytelling & case studies',
        description:
          'Turn customer success stories and brand narratives into compelling video content at scale.',
      },
    ],
  },
]

// ─── Page ───────────────────────────────────────────────────────────────────

export default function SolutionsPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { signIn } = useAuthActions()
  const handleSignUp = () => {
    if (isElectron()) {
      navigate('/signup')
      return
    }
    signIn('google', { redirectTo: `${window.location.origin}/auth` })
  }

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 overflow-x-hidden">
      <Helmet>
        <title>Solutions - Footage | AI Video for Every Industry</title>
        <meta
          name="description"
          content="Discover how Footage AI video generation serves education, legal, healthcare, construction, real estate, and marketing industries. Create professional video content faster."
        />
        <link rel="canonical" href={siteUrl('/solutions')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Solutions - Footage | AI Video for Every Industry" />
        <meta
          property="og:description"
          content="Discover how Footage AI video generation serves education, legal, healthcare, construction, real estate, and marketing industries."
        />
        <meta property="og:url" content={siteUrl('/solutions')} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Solutions - Footage | AI Video for Every Industry" />
        <meta
          name="twitter:description"
          content="Discover how Footage AI video generation serves education, legal, healthcare, construction, real estate, and marketing industries."
        />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <div className="max-w-7xl mx-auto border-x border-border">
        <Navbar isAuthenticated={isAuthenticated} onSignUp={handleSignUp} />
        <main className="flex flex-col divide-y divide-border pt-16">

          {/* ── Header ────────────────────────────────────────────── */}
          <SectionHeader>
            <div className="flex flex-col items-center justify-center gap-4 mt-4">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-medium tracking-tighter text-center text-balance">
                Solutions
              </h1>
              <p className="text-muted-foreground md:text-lg text-center text-balance mx-auto">
                From education to advertising, Footage helps teams across every vertical create professional video content in minutes.
              </p>
            </div>
          </SectionHeader>

          {/* ── Section 1: Industry Overview Grid ─────────────────── */}
          <section className="w-full relative">
            <div className="mx-auto">
              <div className="grid md:grid-cols-6 divide-x divide-border">
                {/* Left — title */}
                <div className="col-span-2 flex flex-col gap-4 p-8 md:p-14">
                  <div className="space-y-4">
                    <h3 className="text-3xl font-medium tracking-tighter md:text-4xl">
                      Six industries, one studio
                    </h3>
                    <p className="text-balance text-muted-foreground">
                      AI video generation that adapts to the unique needs of your industry.
                    </p>
                  </div>
                </div>

                {/* Right — industry cards */}
                <div className="col-span-4 flex flex-col">
                  <div className="relative h-14 border-b overflow-hidden">
                    <div className="absolute inset-0">
                      <LazyDither enableMouseInteraction={false} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {industries.map((industry, i) => {
                      const Icon = industry.icon
                      return (
                        <div
                          key={industry.title}
                          className={cn(
                            "flex flex-col gap-6 p-6 md:p-8",
                            industry.accent ? "bg-accent" : "bg-card/50",
                            i % 2 === 0 && "border-b md:border-r border-border",
                            i % 2 === 1 && "border-b border-border",
                          )}
                        >
                          <div className="flex flex-col gap-4">
                            <div className="size-10 rounded-lg bg-background/60 flex items-center justify-center">
                              <Icon className="size-5" />
                            </div>
                            <h4 className="text-xl font-medium tracking-tight">
                              {industry.title}
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {industry.description}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-auto">
                            {industry.useCases.map((uc) => (
                              <span
                                key={uc.title}
                                className="inline-flex items-center text-xs font-medium text-muted-foreground bg-background/60 px-2.5 py-1 rounded-full border border-border/50"
                              >
                                {uc.title}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="relative h-14 border-t overflow-hidden">
                    <div className="absolute inset-0">
                      <LazyDither enableMouseInteraction={false} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Section 2: Detailed Industry Breakdowns ────────── */}
          <section className="w-full relative">
            <div className="mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-6">
                {/* Left — sticky title */}
                <div className="col-span-1 md:col-span-2 p-8 md:p-10 lg:p-14 md:sticky md:top-20 md:self-start flex flex-col gap-7">
                  <h3 className="text-3xl lg:text-4xl font-medium tracking-tighter text-left text-balance">
                    Built for your workflow
                  </h3>
                  <p className="text-muted-foreground text-left text-balance">
                    Every industry has unique video needs. Footage adapts to each — from patient education to property tours to trial presentations.
                  </p>
                </div>

                {/* Right — industry detail blocks */}
                <div className="col-span-1 md:col-span-4 w-full border-t md:border-t-0 md:border-l border-border relative">
                  <CornerPlus position="all" className="text-muted-foreground/50" />
                  <div className="w-full divide-y divide-border">
                    {industries.map((industry) => {
                      const Icon = industry.icon
                      return (
                        <div key={industry.title} className="relative">
                          <div className="p-8 md:p-12">
                            <div className="w-full max-w-lg space-y-6">
                              {/* Industry header */}
                              <div className="flex items-center gap-3">
                                <div className="size-8 rounded-full bg-foreground flex items-center justify-center">
                                  <Icon className="size-4 text-background" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{industry.title}</p>
                                  <p className="text-xs text-muted-foreground">{industry.tagline}</p>
                                </div>
                              </div>
                              {/* Use cases */}
                              <div className="space-y-2.5">
                                {industry.useCases.map((uc) => (
                                  <div key={uc.title} className="flex items-start gap-3 pl-1">
                                    <ArrowRight className="size-3.5 text-muted-foreground mt-0.5 shrink-0" />
                                    <div>
                                      <p className="text-sm font-medium">{uc.title}</p>
                                      <p className="text-xs text-muted-foreground">{uc.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="max-w-xl text-left items-start p-6 border-t border-border/50">
                            <p className="text-sm text-muted-foreground flex items-center gap-3 justify-start">
                              <Icon className="size-4 text-muted-foreground" />
                              {industry.title}
                            </p>
                            <p className="text-base text-foreground leading-relaxed mt-2">
                              {industry.description}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── CTA ───────────────────────────────────────────────── */}
          <CTA onSignUp={handleSignUp} />
          <HomepageFooter />
        </main>
      </div>
    </div>
  )
}
